import { api } from './api';

export type ItemChecklist = { texto: string; feito: boolean };

export type Tarefa = {
  id: number;
  texto: string;
  responsavelId: number | null;
  responsavelNome: string | null;
  prioridade: string;
  feito: boolean;
  ordem: number;
};

export type QuadroResumo = {
  id: number;
  nome: string;
  chave: string;
  capaCaminho: string | null;
  ordem: number;
  cards: number;
};

export type CardResumo = {
  id: number;
  colunaId: number;
  titulo: string;
  etapa: string;
  prioridade: string;
  entregaEm: string | null;
  linkBruto: string | null;
  linkPronto: string | null;
  linkNome: string | null;
  capaCaminho: string | null;
  checklist: ItemChecklist[];
  ordem: number;
  versao: number;
  responsaveis: { id: number; nome: string }[];
  /** quanto do checklist ja andou — o quadro mostra sem abrir o card */
  tarefas: { feitas: number; total: number };
};

export type Coluna = { id: number; nome: string; chave: string; ordem: number };

export type Quadro = {
  quadro: { id: number; nome: string; chave: string };
  colunas: Coluna[];
  cards: CardResumo[];
  podeEditar: boolean;
};

export type CardCompleto = CardResumo & {
  quadroId: number;
  colunaChave: string;
  colunaNome: string;
  descricaoHtml: string;
  tarefas: Tarefa[];
  comentarios: {
    id: number;
    tipo: string;
    texto: string;
    autor: string | null;
    criadoEm: string | null;
  }[];
  criadoEm: string | null;
  atualizadoEm: string | null;
};

export const ETAPAS: { valor: string; nome: string }[] = [
  { valor: 'ideia', nome: 'Ideia' },
  { valor: 'producao', nome: 'Em produção' },
  { valor: 'revisao', nome: 'Revisão' },
  { valor: 'aprovacao', nome: 'Aprovação' },
  { valor: 'agendado', nome: 'Agendado' },
  { valor: 'entregue', nome: 'Entregue' },
];

export const buscarQuadro = (chave = 'midia') => api<Quadro>(`/quadros/${chave}`);

export const listarQuadros = () =>
  api<{ quadros: QuadroResumo[]; podeEditar: boolean }>('/quadros');

export const criarQuadro = (nome: string) =>
  api<{ quadro: { id: number; nome: string; chave: string } }>('/quadros', {
    method: 'POST',
    body: JSON.stringify({ nome }),
  });

export const salvarQuadro = (
  id: number,
  mudancas: { nome?: string; capaCaminho?: string | null; ordem?: number; arquivado?: boolean },
) => api(`/quadros/${id}`, { method: 'PATCH', body: JSON.stringify(mudancas) });

export const removerQuadro = (id: number) => api(`/quadros/${id}`, { method: 'DELETE' });

export const criarTarefa = (
  cardId: number,
  dados: { texto: string; responsavelId?: number | null; prioridade?: string },
) =>
  api<{ card: CardCompleto }>(`/cards/${cardId}/tarefas`, {
    method: 'POST',
    body: JSON.stringify(dados),
  });

export const salvarTarefa = (
  id: number,
  mudancas: { texto?: string; responsavelId?: number | null; prioridade?: string; feito?: boolean },
) => api<{ card: CardCompleto }>(`/tarefas/${id}`, { method: 'PATCH', body: JSON.stringify(mudancas) });

export const removerTarefa = (id: number) =>
  api<{ card: CardCompleto }>(`/tarefas/${id}`, { method: 'DELETE' });

/** Link do Google Agenda para a entrega do card. */
export function linkGoogleAgenda(titulo: string, inicioIso: string | null, detalhes?: string): string {
  if (!inicioIso) return '';
  const inicio = new Date(inicioIso);
  const fim = new Date(inicio.getTime() + 60 * 60 * 1000);
  const carimbo = (d: Date) => d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  const q = new URLSearchParams({
    action: 'TEMPLATE',
    text: titulo,
    dates: `${carimbo(inicio)}/${carimbo(fim)}`,
    ctz: 'America/Sao_Paulo',
  });
  if (detalhes) q.set('details', detalhes);
  return `https://calendar.google.com/calendar/render?${q.toString()}`;
}
export const buscarCard = (id: number) => api<{ card: CardCompleto }>(`/cards/${id}`);

export const criarCard = (titulo: string, colunaChave: string, quadroChave = 'midia') =>
  api<{ card: CardCompleto }>('/cards', {
    method: 'POST',
    body: JSON.stringify({ titulo, colunaChave, quadroChave }),
  });

/** Salva UM card mandando SO os campos que mudaram. Nunca o quadro inteiro. */
export const salvarCard = (id: number, mudancas: Record<string, unknown>, versaoConhecida?: number) =>
  api<{ card: CardCompleto; alteracoes: string[]; houveMudancaExterna: boolean }>(`/cards/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ ...mudancas, ...(versaoConhecida ? { versaoConhecida } : {}) }),
  });

export const comentarCard = (id: number, texto: string) =>
  api<{ card: CardCompleto }>(`/cards/${id}/comentarios`, {
    method: 'POST',
    body: JSON.stringify({ texto }),
  });

export const arquivarCard = (id: number) => api(`/cards/${id}`, { method: 'DELETE' });

/** Passa o card para outra lista (coluna) do mesmo quadro. */
export const moverCard = (id: number, colunaChave: string) =>
  salvarCard(id, { colunaChave });

export type MinhaDemanda = {
  id: number;
  titulo: string;
  etapa: string;
  prioridade: string;
  entregaEm: string | null;
  colunaNome: string;
  quadroNome: string;
  quadroChave: string;
};

export type MinhaTarefa = {
  id: number;
  texto: string;
  prioridade: string;
  cardId: number;
  cardTitulo: string;
  quadroChave: string;
};

/** O que esta comigo no quadro, para o "Meu dia". */
export const buscarMinhasDemandas = () =>
  api<{ cards: MinhaDemanda[]; tarefas: MinhaTarefa[] }>('/minhas-demandas');

export function formatarData(iso: string | null): string {
  if (!iso) return '';
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

/** ISO local (para input datetime-local) a partir do ISO em UTC. */
export function paraCampoDataHora(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const partes = new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(d);
  const p: Record<string, string> = {};
  for (const parte of partes) if (parte.type !== 'literal') p[parte.type] = parte.value;
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
}

/** O campo datetime-local traz hora de Sao Paulo; devolvemos ISO em UTC. */
export function doCampoDataHora(valor: string): string | null {
  if (!valor) return null;
  const [data, hora] = valor.split('T');
  if (!data || !hora) return null;
  const [ano, mes, dia] = data.split('-').map(Number);
  const [h, m] = hora.split(':').map(Number);
  // descobre o deslocamento de Sao Paulo naquele instante
  const chute = Date.UTC(ano!, mes! - 1, dia!, h!, m!, 0);
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Sao_Paulo',
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const partesDe = (t: number) => {
    const p: Record<string, string> = {};
    for (const parte of fmt.formatToParts(new Date(t))) {
      if (parte.type !== 'literal') p[parte.type] = parte.value;
    }
    return Date.UTC(
      Number(p.year),
      Number(p.month) - 1,
      Number(p.day),
      Number(p.hour) % 24,
      Number(p.minute),
      Number(p.second),
    );
  };
  // ARMADILHA: a correcao tem que partir do valor JA corrigido. Partir do
  // chute na segunda volta desfazia a primeira e a hora andava 3h para tras
  // toda vez que o card era aberto e fechado.
  let t = chute;
  for (let i = 0; i < 2; i++) t = t - (partesDe(t) - chute);
  return new Date(t).toISOString();
}

export type Coluna2 = Coluna;

export const criarColuna = (nome: string, quadroChave = 'midia') =>
  api<{ coluna: Coluna }>('/colunas', {
    method: 'POST',
    body: JSON.stringify({ nome, quadroChave }),
  });

export const salvarColuna = (id: number, mudancas: { nome?: string; ordem?: number }) =>
  api<{ coluna: Coluna }>(`/colunas/${id}`, { method: 'PATCH', body: JSON.stringify(mudancas) });

export const removerColuna = (id: number) => api(`/colunas/${id}`, { method: 'DELETE' });

/** Envia o arquivo cru; o servidor corta se passar do limite. */
export async function enviarArquivo(arquivo: File): Promise<{ caminho: string; url: string }> {
  const r = await fetch('/api/uploads', {
    method: 'POST',
    body: arquivo,
    credentials: 'same-origin',
    headers: { 'Content-Type': arquivo.type || 'application/octet-stream' },
  });
  const texto = await r.text();
  let corpo: any = null;
  try {
    corpo = texto ? JSON.parse(texto) : null;
  } catch {
    corpo = null;
  }
  if (!r.ok) throw new Error(corpo?.mensagem ?? `Falha ao enviar o arquivo (${r.status})`);
  return corpo;
}

export const CORES_DA_ETAPA: Record<string, string> = {
  ideia: 'bg-slate-100 text-slate-600',
  producao: 'bg-amber-100 text-amber-800',
  revisao: 'bg-sky-100 text-sky-800',
  aprovacao: 'bg-violet-100 text-violet-800',
  agendado: 'bg-indigo-100 text-indigo-800',
  entregue: 'bg-tinta-900 text-white',
};

export const NOME_DA_ETAPA: Record<string, string> = Object.fromEntries(
  ETAPAS.map((e) => [e.valor, e.nome]),
);
